import type { ErrorEvent, EventHint } from "@sentry/react";

const MEDIA_PLAYBACK_INTERRUPTION_MESSAGE =
	"The play() request was interrupted by a call to pause().";

const EXTENSION_URL_PROTOCOLS = [
	"chrome-extension://",
	"moz-extension://",
	"safari-extension://",
	"safari-web-extension://",
];

const isUnhandledRejection = (event: ErrorEvent) =>
	(event.exception?.values ?? []).some(
		({ mechanism }) =>
			mechanism?.handled === false &&
			(mechanism.type ?? "").endsWith("onunhandledrejection"),
	);

// Replay clones streaming responses to measure size; a dying stream rejects as
// an uncatchable "TypeError: network error" (APPS-E5H). beforeSend has no
// mapped stacks, so match on message + unhandled-rejection instead.
export const isReplayStreamCloneNoise = (
	event: ErrorEvent,
	hint: EventHint,
) => {
	const error = hint.originalException;
	if (!(error instanceof TypeError) || error.message !== "network error") {
		return false;
	}
	return isUnhandledRejection(event);
};

export const isMediaPlaybackInterruptionNoise = (
	event: ErrorEvent,
	hint: EventHint,
) => {
	const error = hint.originalException;
	// Session navigation pauses outgoing media, so browsers may reject a pending
	// play() promise. This is expected lifecycle noise. Chained events may carry
	// this signature on any exception value, with or without an "Error: " prefix.
	const isInterruptedPlay =
		(typeof DOMException !== "undefined" &&
			error instanceof DOMException &&
			error.name === "AbortError" &&
			error.message.startsWith(MEDIA_PLAYBACK_INTERRUPTION_MESSAGE)) ||
		(error instanceof Error &&
			error.message.startsWith(
				`AbortError: ${MEDIA_PLAYBACK_INTERRUPTION_MESSAGE}`,
			)) ||
		(event.exception?.values ?? []).some((value) =>
			(value.value ?? "")
				.replace(/^Error: /, "")
				.startsWith(`AbortError: ${MEDIA_PLAYBACK_INTERRUPTION_MESSAGE}`),
		);

	return isInterruptedPlay && isUnhandledRejection(event);
};

export const isBrowserExtensionNoise = (event: ErrorEvent) => {
	const filenames = (event.exception?.values ?? [])
		.flatMap((value) => value.stacktrace?.frames ?? [])
		.map((frame) => frame.filename)
		.filter((filename): filename is string => Boolean(filename));

	return (
		filenames.length > 0 &&
		filenames.every((filename) =>
			EXTENSION_URL_PROTOCOLS.some((protocol) => filename.startsWith(protocol)),
		)
	);
};

export const isSentryNoise = (event: ErrorEvent, hint: EventHint) =>
	isReplayStreamCloneNoise(event, hint) ||
	isMediaPlaybackInterruptionNoise(event, hint) ||
	isBrowserExtensionNoise(event);
